import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { fmtPrice, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, remove, setQty, subtotal } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();
  const shipping = items.length ? 1000 : 0;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-14 h-14 mx-auto text-stone-300 mb-4" />
        <h1 className="font-display font-extrabold text-2xl mb-2">{t("cart_empty")}</h1>
        <Link to="/catalog"><Button className="rounded-full bg-emerald-800 hover:bg-emerald-900 mt-4">{t("continue_shopping")}</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-8">{t("cart")} <span className="text-stone-400 text-xl">({items.length} {t("items")})</span></h1>
      <div className="grid lg:grid-cols-[1fr_340px] gap-8">
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 bg-white border border-stone-200 rounded-xl p-3" data-testid={`cart-item-${it.product_id}`}>
              <img src={mediaUrl(it.image)} alt={it.name} className="w-20 h-20 rounded-lg object-cover bg-stone-100" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 line-clamp-1">{it.name}</p>
                <p className="text-xs text-stone-400">{it.seller_name}</p>
                <p className="font-display font-bold text-emerald-800 mt-1">{fmtPrice(it.price)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => remove(it.product_id)} className="text-stone-400 hover:text-red-600" data-testid={`remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
                <div className="flex items-center border border-stone-300 rounded-full">
                  <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="w-8 h-8 grid place-items-center"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-8 text-center text-sm font-semibold">{it.quantity}</span>
                  <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="w-8 h-8 grid place-items-center"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 h-fit sticky top-24">
          <h2 className="font-display font-bold text-lg mb-4">{t("order_summary")}</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">{t("subtotal")}</span><span className="font-medium">{fmtPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">{t("shipping")}</span><span className="font-medium">{fmtPrice(shipping)}</span></div>
            <div className="border-t border-stone-200 pt-3 flex justify-between text-base"><span className="font-semibold">{t("total")}</span><span className="font-display font-extrabold text-emerald-800">{fmtPrice(subtotal + shipping)}</span></div>
          </div>
          <Button onClick={() => navigate("/checkout")} className="w-full rounded-full bg-mango-600 hover:bg-mango-700 h-12 mt-5" data-testid="checkout-btn">{t("checkout")}</Button>
          <Link to="/catalog"><Button variant="ghost" className="w-full mt-2">{t("continue_shopping")}</Button></Link>
        </div>
      </div>
    </div>
  );
}
